import type { PanelWhatsappOnboardingFailureReason } from '@vaptmenu/analytics-react';
import type { ApiError } from '@/shared/lib/problem-details';

/* ==========================================================================
   Códigos de erro do canal e do aceite

   O mesmo texto de `WhatsappErrorCodes`, `ApiErrors.WhatsappDisabled`,
   `ApiErrors.LegalAcceptanceRequired` e do `RequirePlanFeature` na API
   (contrato docs/atendente-ia.md §4.13). O painel classifica pelo
   `errorCode` do ProblemDetails, e não pelo status: o 409 serve a muitas
   recusas diferentes.
   ========================================================================== */

export const WHATSAPP_ERRORS = {
  /** 404: canal desligado, flag desligada para a loja ou sem tenant. A área some. */
  disabled: 'whatsapp.disabled',
  notConnected: 'whatsapp.not_connected',
  alreadyConnected: 'whatsapp.already_connected',
  signupSessionExpired: 'whatsapp.signup_session_expired',
  signupSessionUsed: 'whatsapp.signup_session_used',
  phoneNumberInUse: 'whatsapp.phone_number_in_use',
  numberNotEligible: 'whatsapp.number_not_eligible',
  metaError: 'whatsapp.meta_error',
} as const;

export const LEGAL_ERRORS = {
  /** 409 com `documents[]`: os documentos que a loja ainda não aceitou na versão vigente. */
  acceptanceRequired: 'legal.acceptance_required',
  /** 409: a versão enviada não é mais a vigente (painel aberto há dias). */
  versionOutdated: 'legal.version_outdated',
  unknownDocument: 'legal.unknown_document',
} as const;

/** 403 do `RequirePlanFeature` (billing-plans): o plano da loja não tem o recurso. */
export const PLAN_UPGRADE_REQUIRED = 'billing.plan_upgrade_required';

/** O `errorCode` do ProblemDetails, quando há. */
export function apiErrorCode(error: unknown): string | undefined {
  const code = (error as ApiError | undefined)?.problem?.errorCode;
  return typeof code === 'string' ? code : undefined;
}

export function apiErrorStatus(error: unknown): number | undefined {
  const status = (error as ApiError | undefined)?.problem?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Recusa do `complete` → motivo de `panel.whatsapp_onboarding_failed`, como
 * dado (spec `product-analytics`, "Eventos de conexão do WhatsApp confirmados
 * pela API"). Só estes três códigos têm membro em `WhatsappConnectionReason`;
 * as demais recusas (sessão vencida ou usada, conexão já existente, plano,
 * aceite, 404 da sessão) não geram evento.
 */
export const COMPLETE_FAILURE_REASONS: Readonly<Record<string, PanelWhatsappOnboardingFailureReason>> = {
  [WHATSAPP_ERRORS.phoneNumberInUse]: 'phone_number_in_use',
  [WHATSAPP_ERRORS.numberNotEligible]: 'number_not_eligible',
  [WHATSAPP_ERRORS.metaError]: 'meta_error',
};

/** O motivo do evento para uma recusa do `complete`, ou `undefined` quando a recusa não gera evento. */
export function completeFailureReason(error: unknown): PanelWhatsappOnboardingFailureReason | undefined {
  const code = apiErrorCode(error);
  return code !== undefined && Object.hasOwn(COMPLETE_FAILURE_REASONS, code)
    ? COMPLETE_FAILURE_REASONS[code]
    : undefined;
}

/**
 * Documentos pendentes do 409 `legal.acceptance_required`, pelo nome de fio.
 * Vazio quando a recusa é outra.
 */
export function pendingLegalDocuments(error: unknown): string[] {
  if (apiErrorCode(error) !== LEGAL_ERRORS.acceptanceRequired) return [];
  const documents = (error as ApiError).problem.documents;
  return Array.isArray(documents) ? documents.filter((d): d is string => typeof d === 'string') : [];
}
